class WebContainerService {
  constructor() {
    this.container = null;
    this.isBooted = false;
    this.bootPromise = null;
  }

  async boot() {
    if (this.isBooted) return this.container;
    if (this.bootPromise) return this.bootPromise;

    this.bootPromise = this._initializeContainer();
    return this.bootPromise;
  }

  async _initializeContainer() {
    try {
      // Check if WebContainer is available
      if (typeof window !== 'undefined' && window.WebContainer) {
        const { WebContainer } = await import('@webcontainer/api');
        this.container = await WebContainer.boot();
        this.isBooted = true;
        console.log('WebContainer booted successfully');
        return this.container;
      } else {
        // Fallback to mock container for development
        console.warn('WebContainer not available, using mock container');
        this.container = new MockWebContainer();
        this.isBooted = true;
        return this.container;
      }
    } catch (error) {
      console.error('Failed to boot WebContainer:', error);
      // Use mock container as fallback
      this.container = new MockWebContainer();
      this.isBooted = true;
      return this.container;
    }
  }

  async createProject(projectId, initialFiles = {}) {
    const container = await this.boot();
    
    const defaultFiles = {
      'package.json': {
        file: {
          contents: JSON.stringify({
            "name": `project-${projectId}`,
            "version": "1.0.0",
            "description": "Generated project",
            "main": "index.js",
            "scripts": {
              "start": "node index.js",
              "dev": "node index.js",
              "build": "echo 'Build completed'"
            },
            "dependencies": {
              "express": "^4.18.0"
            }
          }, null, 2)
        }
      },
      'index.js': {
        file: {
          contents: `const express = require('express');
const path = require('path');
const app = express();
const port = process.env.PORT || 3111;

// Serve static files
app.use(express.static('public'));

app.get('/', (req, res) => {
  res.send(\`
    <html>
      <head>
        <title>Project ${projectId}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; }
          .container { max-width: 800px; margin: 0 auto; }
          .header { color: #333; border-bottom: 2px solid #007acc; padding-bottom: 10px; }
          .content { margin-top: 20px; line-height: 1.6; }
          .status { background: #e7f5e7; padding: 10px; border-radius: 5px; margin: 20px 0; }
        </style>
      </head>
      <body>
        <div class="container">
          <h1 class="header">🚀 Welcome to Project ${projectId}!</h1>
          <div class="status">
            ✅ WebContainer is running successfully<br>
            🔧 Server started on port \${port}<br>
            📝 Ready for development
          </div>
          <div class="content">
            <h2>Getting Started</h2>
            <p>Your project is now running in a WebContainer environment. You can:</p>
            <ul>
              <li>Edit files in the code editor</li>
              <li>Install npm packages</li>
              <li>Run build commands</li>
              <li>See live updates in this preview</li>
            </ul>
            <p><strong>Last updated:</strong> \${new Date().toLocaleString()}</p>
          </div>
        </div>
      </body>
    </html>
  \`);
});

app.listen(port, () => {
  console.log(\`🚀 App is live at http://localhost:\${port}\`);
});`
        }
      },
      'README.md': {
        file: {
          contents: `# Project ${projectId}

This project was generated using the Bolt-style development environment.

## Getting Started

1. Install dependencies: \`npm install\`
2. Start the development server: \`npm run dev\`
3. Open your browser to see the live preview

## Features

- Live code editing with Monaco Editor
- Real-time preview
- Terminal access
- File system management
- WebContainer integration

## Commands

- \`npm start\` - Start the application
- \`npm run dev\` - Start development server
- \`npm run build\` - Build the application

Happy coding! 🎉`
        }
      }
    };

    // Merge with initial files
    const filesToMount = { ...defaultFiles, ...initialFiles };
    
    await container.mount(filesToMount);
    return container;
  }

  async installDependencies() {
    const container = await this.boot();
    if (container.spawn) {
      const installProcess = await container.spawn('npm', ['install']);
      return installProcess.exit;
    }
    return Promise.resolve(0);
  }

  async startDevServer() {
    const container = await this.boot();
    if (container.spawn) {
      const serverProcess = await container.spawn('npm', ['run', 'dev']);
      return serverProcess;
    }
    return null;
  }

  async runCommand(command, args = []) {
    const container = await this.boot();
    if (container.spawn) {
      return container.spawn(command, args);
    }
    return null;
  }

  getContainer() {
    return this.container;
  }

  isReady() {
    return this.isBooted && this.container !== null;
  }
}

// Mock WebContainer for fallback
class MockWebContainer {
  constructor() {
    this.files = new Map();
    this.processes = new Map();
  }

  async mount(files) {
    Object.entries(files).forEach(([path, descriptor]) => {
      if (descriptor.file) {
        this.files.set(path, descriptor.file.contents);
      }
    });
    console.log('Mock: Mounted files', Object.keys(files));
  }

  get fs() {
    return {
      readFile: async (path, encoding) => {
        const content = this.files.get(path);
        if (content === undefined) {
          throw new Error(`File not found: ${path}`);
        }
        return encoding === 'utf-8' ? content : new TextEncoder().encode(content);
      },
      
      writeFile: async (path, content) => {
        this.files.set(path, content);
        console.log('Mock: Wrote file', path);
      },
      
      readdir: async (path, options) => {
        const entries = [];
        const prefix = path === '' ? '' : path + '/';
        
        for (const filePath of this.files.keys()) {
          if (filePath.startsWith(prefix)) {
            const relativePath = filePath.substring(prefix.length);
            const parts = relativePath.split('/');
            if (parts.length === 1 && parts[0]) {
              entries.push({
                name: parts[0],
                isDirectory: () => false,
                isFile: () => true
              });
            }
          }
        }
        
        return entries;
      }
    };
  }

  async spawn(command, args) {
    console.log('Mock: Spawning', command, args);
    
    const process = {
      exit: Promise.resolve(0),
      output: new ReadableStream({
        start(controller) {
          // Simulate command output
          const messages = [
            `Mock: Running ${command} ${args?.join(' ') || ''}`,
            'Mock: Process completed successfully'
          ];
          
          messages.forEach(msg => {
            controller.enqueue(new TextEncoder().encode(msg + '\n'));
          });
          
          setTimeout(() => controller.close(), 1000);
        }
      }),
      input: {
        getWriter: () => ({
          write: (data) => console.log('Mock: Input', data)
        })
      }
    };
    
    this.processes.set(Date.now(), process);
    return process;
  }
}

// Export singleton instance
export const webContainerService = new WebContainerService();
export default webContainerService;